import React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Box } from '@material-ui/core'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { validators } from '../../validation/validators'
import { WizardStep, WizardStepProps } from '../form/WizardStep'
import { PurchasedEnergyType } from '../../generated/graphql'
import { formatMonth } from '../common/util/date-util'

export type InternalPricingModelEnergyPriceFormValues = {
  energyType: PurchasedEnergyType
  pricesPerMwh: string[]
}

export type ExternalPricingModelEnergyPriceFormValues = {
  prices: InternalPricingModelEnergyPriceFormValues[]
  sellingPrices: InternalPricingModelEnergyPriceFormValues[]
}

export type PricingModelEnergyPriceStepProps =
  WizardStepProps<ExternalPricingModelEnergyPriceFormValues> & {
    onSubmit: (values: InternalPricingModelEnergyPriceFormValues) => void
    title: string
    arrayName: keyof ExternalPricingModelEnergyPriceFormValues
    arrayIndex: number
  }

export function PricingModelEnergyPriceStep(
  props: PricingModelEnergyPriceStepProps,
): React.ReactElement {
  const { defaultValues, onSubmit, title, arrayName, arrayIndex } = props
  const { t } = useTranslation()

  const internalDefaultValues: Partial<InternalPricingModelEnergyPriceFormValues> =
    defaultValues?.[arrayName]?.[arrayIndex] ?? {}
  const { control, handleSubmit, errors } =
    useForm<InternalPricingModelEnergyPriceFormValues>({
      defaultValues: internalDefaultValues,
    })

  return (
    <WizardStep
      {...props}
      title={title}
      onSubmit={handleSubmit(onSubmit)}
      dataCy={`${arrayName}-${arrayIndex}-energy-prices-step`}
    >
      <Box display="none">
        <ControlledTextField
          control={control}
          name={`${arrayName}[${arrayIndex}].energyType"`}
          rules={validators.required}
          error={errors.energyType}
          defaultValue={
            internalDefaultValues.energyType ?? PurchasedEnergyType.Heating
          }
        />
      </Box>
      {Array.from(Array(12).keys()).map((monthIndex) => (
        <FormGroup key={monthIndex}>
          <ControlledTextField
            control={control}
            name={`${arrayName}[${arrayIndex}].pricesPerMwh[${monthIndex}]"`}
            rules={validators.required}
            error={errors.pricesPerMwh?.[monthIndex]}
            label={`${t('pricingModels.pricesPerMwh')} | ${formatMonth(
              monthIndex,
              t('date.month'),
            )}`}
            defaultValue={
              internalDefaultValues.pricesPerMwh?.[monthIndex] ?? '0'
            }
          />
        </FormGroup>
      ))}
    </WizardStep>
  )
}
